import React, { useEffect, useState } from "react";
import { X, Check } from "lucide-react";

/**
 * Popup minimal per rinominare le 5 fonti di denaro.
 * Non tocca i saldi, solo le label. Salva su onSave(labels[]).
 * Styling coerente con il TIME Watch (nero opaco, emerald, tipografia mono).
 */
export default function CustomizeAccountsDialog({ open, onClose, accounts, onSave }) {
  const [labels, setLabels] = useState(() => accounts.map((a) => a.label));

  useEffect(() => {
    if (open) {
      setLabels(accounts.map((a) => a.label));
    }
  }, [open, accounts]);

  if (!open) return null;

  const handleSave = () => {
    onSave(labels);
    onClose();
  };

  const handleChange = (i) => (e) => {
    const v = e.target.value.slice(0, 40);
    setLabels((prev) => prev.map((l, idx) => (idx === i ? v : l)));
  };

  return (
    <div
      data-testid="customize-accounts-dialog"
      className="fixed inset-0 z-[70] flex items-center justify-center px-4 sm:px-6"
      role="dialog"
      aria-modal="true"
    >
      {/* backdrop */}
      <div
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* card */}
      <div className="relative w-full max-w-md rounded-2xl bg-zinc-950 border border-white/10 shadow-[0_0_60px_rgba(16,185,129,0.08)] p-6 sm:p-8">
        <button
          data-testid="customize-close"
          onClick={onClose}
          className="absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center text-zinc-400 hover:text-white hover:bg-white/5 transition-colors"
          aria-label="Chiudi"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="mb-6">
          <div className="text-[10px] tracking-[0.32em] uppercase text-emerald-400/80 font-mono mb-2">
            TIME Watch · Patrimonio
          </div>
          <h3
            data-testid="customize-title"
            className="text-xl sm:text-2xl font-light tracking-tight text-white leading-tight"
          >
            Personalizza le tue 5 fonti di denaro
          </h3>
          <p className="mt-3 text-xs sm:text-sm text-zinc-500 leading-relaxed">
            Assegna un nome alle cinque fonti in base alla tua situazione personale.
            Le modifiche si sincronizzano automaticamente su tutti i dispositivi.
          </p>
        </div>

        <div className="space-y-3">
          {accounts.map((a, i) => (
            <div key={a.id} className="flex items-center gap-3">
              <span className="w-6 text-[10px] tracking-[0.2em] uppercase text-zinc-500 font-mono">
                {String(i + 1).padStart(2, "0")}
              </span>
              <input
                data-testid={`customize-input-${i}`}
                type="text"
                value={labels[i] || ""}
                onChange={handleChange(i)}
                placeholder={`Fonte ${i + 1}`}
                maxLength={40}
                className="flex-1 px-3 py-2 rounded-lg bg-black/40 border border-white/10 text-sm text-white placeholder-white/25 focus:border-emerald-400/40 focus:outline-none transition-colors"
              />
            </div>
          ))}
        </div>

        <div className="mt-8 flex items-center justify-end gap-3">
          <button
            data-testid="customize-cancel"
            onClick={onClose}
            className="px-4 py-2 text-[11px] tracking-[0.24em] uppercase text-zinc-500 hover:text-white transition-colors font-mono"
          >
            Annulla
          </button>
          <button
            data-testid="customize-save"
            onClick={handleSave}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-emerald-400/90 text-black text-[11px] tracking-[0.24em] uppercase font-medium hover:bg-emerald-400 transition-all"
          >
            <Check className="w-3.5 h-3.5" />
            Salva
          </button>
        </div>
      </div>
    </div>
  );
}
